import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  AccessTime as AccessTimeIcon,
  Cancel as CancelIcon,
  CardGiftcard as CardGiftcardIcon,
  ChevronRight as ChevronRightIcon,
  Discount as DiscountIcon,
  EventBusy as EventBusyIcon,
  Restore as RestoreIcon,
  Subscriptions as SubscriptionsIcon,
  SwapHoriz as SwapHorizIcon,
  TrendingUp as TrendingUpIcon,
  Tune as TuneIcon,
} from '@mui/icons-material'
import {
  subscriptionService,
  type SubscriptionDto,
  type SubscriptionPlanDto,
} from '../api/adminServices'
import ConfirmDialog from './ConfirmDialog'
import { useToast } from './useToast'

type DialogKind =
  | 'changePlan'
  | 'extendTrial'
  | 'forceCancel'
  | 'reactivate'
  | 'applyDiscount'
  | 'freePeriod'
  | 'billingOptions'
  | 'bouncePlan'
  | null

interface Props {
  accountId: string | null
  accountType: string
  subscriptions: SubscriptionDto[]
  plans: SubscriptionPlanDto[]
  submenuAnchor: HTMLElement | null
  parentMenuOpen: boolean
  onSubmenuClose: () => void
  onCloseAll: () => void
}

export function AccountSubscriptionMenuEntry({
  onOpenSubmenu,
}: {
  onOpenSubmenu: (el: HTMLElement) => void
}) {
  return (
    <MenuItem
      onMouseEnter={(e) => onOpenSubmenu(e.currentTarget)}
      onClick={(e) => onOpenSubmenu(e.currentTarget)}
    >
      <SubscriptionsIcon sx={{ mr: 1 }} fontSize="small" />
      Subscriptions
      <ChevronRightIcon sx={{ ml: 'auto', fontSize: 18, color: 'text.secondary' }} />
    </MenuItem>
  )
}

export default function AccountSubscriptionActions({
  accountId,
  accountType,
  subscriptions,
  plans,
  submenuAnchor,
  parentMenuOpen,
  onSubmenuClose,
  onCloseAll,
}: Props) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const [activeDialog, setActiveDialog] = useState<DialogKind>(null)
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false)

  const [newPlanId, setNewPlanId] = useState('')
  const [changePlanProrate, setChangePlanProrate] = useState(true)
  const [changePlanStartTrial, setChangePlanStartTrial] = useState(false)
  const [changePlanReason, setChangePlanReason] = useState('')

  const [extendDays, setExtendDays] = useState('7')
  const [cancelReason, setCancelReason] = useState('')

  // Apply-discount dialog state
  const [discountAmount, setDiscountAmount] = useState('')
  const [discountPercentage, setDiscountPercentage] = useState('')
  const [discountScope, setDiscountScope] = useState<'TOTAL' | 'MONTHLY_FEE' | 'USAGE'>('TOTAL')
  const [discountEndDate, setDiscountEndDate] = useState('')
  const [discountReason, setDiscountReason] = useState('')

  // Free-period dialog state
  const [freeDays, setFreeDays] = useState('30')
  const [freeReason, setFreeReason] = useState('')

  // Billing-options dialog state
  const [chargeMonthlyOnly, setChargeMonthlyOnly] = useState(false)
  const [bonusTons, setBonusTons] = useState('')
  const [bonusJobs, setBonusJobs] = useState('')
  const [overageDiscount, setOverageDiscount] = useState('')
  const [billingReason, setBillingReason] = useState('')

  // Bounce-plan dialog state
  const [bouncePlanId, setBouncePlanId] = useState('')
  const [bounceDays, setBounceDays] = useState('30')
  const [bounceReason, setBounceReason] = useState('')

  const trialSub = subscriptions.find((s) => s.status === 'TRIAL')
  const activeLikeSub = subscriptions.find((s) =>
    ['TRIAL', 'ACTIVE', 'PAST_DUE'].includes(s.status),
  )
  const cancelableSub = subscriptions.find(
    (s) => s.status !== 'CANCELLED' && s.status !== 'EXPIRED',
  )
  const reactivatableSub = subscriptions.find((s) =>
    ['CANCELLED', 'EXPIRED', 'SUSPENDED'].includes(s.status),
  )

  const accountTypeUpper = accountType.toUpperCase()
  const eligiblePlans = plans.filter(
    (p) => p.isActive !== false && (!p.accountType || p.accountType === accountTypeUpper),
  )
  const selectedTargetPlan = eligiblePlans.find((p) => p.id === newPlanId)

  const invalidateSubscriptions = () => {
    void queryClient.invalidateQueries({ queryKey: ['subscriptions-for-accounts'] })
    void queryClient.invalidateQueries({ queryKey: ['subscriptions'] })
    if (accountId) {
      void queryClient.invalidateQueries({ queryKey: ['account-subscriptions', accountId] })
    }
  }

  const closeDialog = () => {
    setActiveDialog(null)
    setCancelConfirmOpen(false)
  }

  const changePlanMutation = useMutation({
    mutationFn: () =>
      subscriptionService.changePlan(accountId!, {
        newPlanId,
        prorate: changePlanProrate,
        startTrial: changePlanStartTrial,
        reason: changePlanReason.trim() || undefined,
      }),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Plan changed')
      setNewPlanId('')
      setChangePlanReason('')
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to change plan'),
  })

  const extendTrialMutation = useMutation({
    mutationFn: () =>
      subscriptionService.extendTrial(trialSub!.id, parseInt(extendDays, 10)),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success(`Trial extended by ${extendDays} days`)
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to extend trial'),
  })

  const forceCancelMutation = useMutation({
    mutationFn: () =>
      subscriptionService.forceCancel(cancelableSub!.id, cancelReason.trim()),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Subscription cancelled')
      setCancelReason('')
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to cancel subscription'),
  })

  const reactivateMutation = useMutation({
    mutationFn: () => subscriptionService.reactivate(reactivatableSub!.id),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Subscription reactivated')
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to reactivate subscription'),
  })

  const applyDiscountMutation = useMutation({
    mutationFn: () =>
      subscriptionService.applyDiscount(activeLikeSub!.id, {
        discountAmount: discountAmount ? parseFloat(discountAmount) : undefined,
        discountPercentage: discountPercentage ? parseFloat(discountPercentage) : undefined,
        discountScope,
        discountEndDate: discountEndDate || undefined,
        reason: discountReason.trim(),
      }),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Discount applied')
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to apply discount'),
  })

  const grantFreePeriodMutation = useMutation({
    mutationFn: () =>
      subscriptionService.grantFreePeriod(activeLikeSub!.id, {
        days: parseInt(freeDays, 10),
        reason: freeReason.trim(),
      }),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Free period granted')
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to grant free period'),
  })

  const endFreePeriodMutation = useMutation({
    mutationFn: () => subscriptionService.endFreePeriod(activeLikeSub!.id),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Free period ended')
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to end free period'),
  })

  const setBillingOptionsMutation = useMutation({
    mutationFn: () =>
      subscriptionService.setBillingOptions(activeLikeSub!.id, {
        chargeMonthlyFeeOnly: chargeMonthlyOnly,
        bonusIncludedTons: bonusTons ? parseFloat(bonusTons) : undefined,
        bonusIncludedJobs: bonusJobs ? parseInt(bonusJobs, 10) : undefined,
        overageDiscountPercentage: overageDiscount ? parseFloat(overageDiscount) : undefined,
        reason: billingReason.trim(),
      }),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Billing options updated')
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to update billing options'),
  })

  const bouncePlanMutation = useMutation({
    mutationFn: () =>
      subscriptionService.bouncePlan(activeLikeSub!.id, {
        newPlanId: bouncePlanId,
        days: parseInt(bounceDays, 10),
        reason: bounceReason.trim(),
      }),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Plan bounced')
      closeDialog()
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to bounce plan'),
  })

  const revertBounceMutation = useMutation({
    mutationFn: () => subscriptionService.revertBounce(activeLikeSub!.id),
    onSuccess: () => {
      invalidateSubscriptions()
      toast.success('Plan bounce reverted')
      onCloseAll()
    },
    onError: (e) => toast.errorFrom(e, 'Failed to revert bounce'),
  })

  const openDialog = (kind: DialogKind) => {
    onSubmenuClose()
    setActiveDialog(kind)
  }

  const submenuOpen = parentMenuOpen && Boolean(submenuAnchor)

  return (
    <>
      <Menu
        anchorEl={submenuAnchor}
        open={submenuOpen}
        onClose={onSubmenuClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        MenuListProps={{ onMouseLeave: onSubmenuClose }}
      >
        <MenuItem
          disabled={!accountId}
          onClick={() => {
            setNewPlanId('')
            setChangePlanProrate(true)
            setChangePlanStartTrial(false)
            setChangePlanReason('')
            openDialog('changePlan')
          }}
        >
          <SwapHorizIcon sx={{ mr: 1 }} fontSize="small" />
          Change Plan
        </MenuItem>
        {trialSub && (
          <MenuItem onClick={() => openDialog('extendTrial')}>
            <AccessTimeIcon sx={{ mr: 1 }} fontSize="small" />
            Extend Trial
          </MenuItem>
        )}
        {activeLikeSub && (
          <MenuItem
            onClick={() => {
              setDiscountAmount('')
              setDiscountPercentage('')
              setDiscountScope('TOTAL')
              setDiscountEndDate('')
              setDiscountReason('')
              openDialog('applyDiscount')
            }}
          >
            <DiscountIcon sx={{ mr: 1 }} fontSize="small" />
            Apply Discount
          </MenuItem>
        )}
        {activeLikeSub && (
          <MenuItem
            onClick={() => {
              setFreeDays('30')
              setFreeReason('')
              openDialog('freePeriod')
            }}
          >
            <CardGiftcardIcon sx={{ mr: 1 }} fontSize="small" />
            Grant Free Period
          </MenuItem>
        )}
        {activeLikeSub?.freeUntilDate && (
          <MenuItem
            onClick={() => {
              onSubmenuClose()
              endFreePeriodMutation.mutate()
            }}
          >
            <EventBusyIcon sx={{ mr: 1 }} fontSize="small" />
            End Free Period
          </MenuItem>
        )}
        {activeLikeSub && (
          <MenuItem
            onClick={() => {
              setChargeMonthlyOnly(Boolean(activeLikeSub.chargeMonthlyFeeOnly))
              setBonusTons(
                activeLikeSub.bonusIncludedTons != null
                  ? String(activeLikeSub.bonusIncludedTons)
                  : '',
              )
              setBonusJobs(
                activeLikeSub.bonusIncludedJobs != null
                  ? String(activeLikeSub.bonusIncludedJobs)
                  : '',
              )
              setOverageDiscount(
                activeLikeSub.overageDiscountPercentage != null
                  ? String(activeLikeSub.overageDiscountPercentage)
                  : '',
              )
              setBillingReason('')
              openDialog('billingOptions')
            }}
          >
            <TuneIcon sx={{ mr: 1 }} fontSize="small" />
            Billing Options
          </MenuItem>
        )}
        {activeLikeSub && !activeLikeSub.bounceOriginalPlanId && (
          <MenuItem
            onClick={() => {
              setBouncePlanId('')
              setBounceDays('30')
              setBounceReason('')
              openDialog('bouncePlan')
            }}
          >
            <TrendingUpIcon sx={{ mr: 1 }} fontSize="small" />
            Temporary Plan Bounce
          </MenuItem>
        )}
        {activeLikeSub?.bounceOriginalPlanId && (
          <MenuItem
            onClick={() => {
              onSubmenuClose()
              revertBounceMutation.mutate()
            }}
          >
            <RestoreIcon sx={{ mr: 1 }} fontSize="small" />
            Revert Bounce
          </MenuItem>
        )}
        {cancelableSub && (
          <MenuItem
            onClick={() => {
              setCancelReason('')
              openDialog('forceCancel')
            }}
          >
            <CancelIcon sx={{ mr: 1 }} fontSize="small" />
            Force Cancel
          </MenuItem>
        )}
        {reactivatableSub && (
          <MenuItem onClick={() => openDialog('reactivate')}>
            <RestoreIcon sx={{ mr: 1 }} fontSize="small" />
            Reactivate
          </MenuItem>
        )}
      </Menu>

      {/* Change Plan */}
      <Dialog
        open={activeDialog === 'changePlan'}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Change Plan</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <TextField
              select
              fullWidth
              required
              label="New plan"
              value={newPlanId}
              onChange={(e) => setNewPlanId(e.target.value)}
            >
              {eligiblePlans.length === 0 ? (
                <MenuItem value="" disabled>
                  No plans for {accountTypeUpper || 'this account type'}
                </MenuItem>
              ) : (
                eligiblePlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                    {plan.planCode ? ` (${plan.planCode})` : ''}
                  </MenuItem>
                ))
              )}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={changePlanProrate}
                  onChange={(e) => setChangePlanProrate(e.target.checked)}
                />
              }
              label="Prorate billing for plan difference"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={changePlanStartTrial}
                  onChange={(e) => setChangePlanStartTrial(e.target.checked)}
                  disabled={!selectedTargetPlan?.trialPeriodDays}
                />
              }
              label={
                selectedTargetPlan?.trialPeriodDays
                  ? `Start trial (${selectedTargetPlan.trialPeriodDays} days) instead of activating immediately`
                  : 'Start trial (selected plan has no trial period)'
              }
            />
            <TextField
              fullWidth
              label="Reason"
              value={changePlanReason}
              onChange={(e) => setChangePlanReason(e.target.value)}
              multiline
              rows={2}
              placeholder="Optional note for audit log"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!newPlanId || !accountId || changePlanMutation.isPending}
            onClick={() => changePlanMutation.mutate()}
          >
            {changePlanMutation.isPending ? 'Changing…' : 'Change Plan'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend Trial */}
      <Dialog
        open={activeDialog === 'extendTrial'}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Extend Trial</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Extend the trial period for this account&apos;s subscription.
          </Typography>
          <TextField
            fullWidth
            label="Days to extend"
            type="number"
            value={extendDays}
            onChange={(e) => setExtendDays(e.target.value)}
            inputProps={{ min: 1, max: 365 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<AccessTimeIcon />}
            disabled={
              !extendDays ||
              parseInt(extendDays, 10) < 1 ||
              extendTrialMutation.isPending ||
              !trialSub
            }
            onClick={() => extendTrialMutation.mutate()}
          >
            {extendTrialMutation.isPending ? 'Extending…' : 'Extend Trial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Cancel */}
      <Dialog
        open={activeDialog === 'forceCancel'}
        onClose={closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle color="error">Force Cancel Subscription</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This immediately cancels the subscription. Billing stops and the account loses
            plan access.
          </Typography>
          <TextField
            fullWidth
            required
            label="Cancellation reason"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            multiline
            rows={3}
            placeholder="Explain why this subscription is being force-cancelled"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Back</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!cancelReason.trim() || !cancelableSub}
            onClick={() => setCancelConfirmOpen(true)}
          >
            Force Cancel…
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={cancelConfirmOpen}
        title="Force-cancel subscription?"
        description={
          <>
            This cannot be undone.
            {cancelReason ? (
              <>
                {' '}
                Reason: <strong>{cancelReason}</strong>
              </>
            ) : null}
          </>
        }
        confirmPhrase="CANCEL"
        confirmHelper="Type CANCEL to confirm."
        confirmLabel="Force Cancel"
        severity="error"
        loading={forceCancelMutation.isPending}
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => forceCancelMutation.mutate()}
      />

      {/* Reactivate */}
      <Dialog
        open={activeDialog === 'reactivate'}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reactivate Subscription</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Restore subscription access for this account. The subscription will return to an
            active billing state.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<RestoreIcon />}
            disabled={!reactivatableSub || reactivateMutation.isPending}
            onClick={() => reactivateMutation.mutate()}
          >
            {reactivateMutation.isPending ? 'Reactivating…' : 'Reactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Apply Discount */}
      <Dialog open={activeDialog === 'applyDiscount'} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Apply Discount</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Set a flat amount and/or a percentage. Choose whether the discount targets the
              monthly fee, usage charges (per-ton / per-job), or the whole invoice.
            </Typography>
            <TextField
              select
              fullWidth
              label="Applies to"
              value={discountScope}
              onChange={(e) =>
                setDiscountScope(e.target.value as 'TOTAL' | 'MONTHLY_FEE' | 'USAGE')
              }
            >
              <MenuItem value="TOTAL">Whole invoice (monthly + usage)</MenuItem>
              <MenuItem value="MONTHLY_FEE">Monthly fee only</MenuItem>
              <MenuItem value="USAGE">Usage only (per-ton / per-job)</MenuItem>
            </TextField>
            <TextField
              fullWidth
              label="Discount amount"
              type="number"
              value={discountAmount}
              onChange={(e) => setDiscountAmount(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
            />
            <TextField
              fullWidth
              label="Discount percentage"
              type="number"
              value={discountPercentage}
              onChange={(e) => setDiscountPercentage(e.target.value)}
              inputProps={{ min: 0, max: 100, step: '0.01' }}
            />
            <TextField
              fullWidth
              label="Discount end date"
              type="date"
              value={discountEndDate}
              onChange={(e) => setDiscountEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leave empty for an open-ended discount"
            />
            <TextField
              fullWidth
              required
              label="Reason"
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          {activeLikeSub &&
            (activeLikeSub.discountAmount || activeLikeSub.discountPercentage) && (
              <Button
                color="warning"
                onClick={() => subscriptionService.removeDiscount(activeLikeSub.id).then(() => {
                  invalidateSubscriptions()
                  toast.success('Discount removed')
                  closeDialog()
                  onCloseAll()
                }).catch((e) => toast.errorFrom(e, 'Failed to remove discount'))}
              >
                Remove existing
              </Button>
            )}
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              !discountReason.trim() ||
              (!discountAmount && !discountPercentage) ||
              !activeLikeSub ||
              applyDiscountMutation.isPending
            }
            onClick={() => applyDiscountMutation.mutate()}
          >
            {applyDiscountMutation.isPending ? 'Applying…' : 'Apply Discount'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Grant Free Period */}
      <Dialog open={activeDialog === 'freePeriod'} onClose={closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Grant Free Period</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              The account is billed nothing (100% off) for the granted period.
            </Typography>
            <TextField
              fullWidth
              label="Free days from today"
              type="number"
              value={freeDays}
              onChange={(e) => setFreeDays(e.target.value)}
              inputProps={{ min: 1, max: 3650 }}
            />
            <TextField
              fullWidth
              required
              label="Reason"
              value={freeReason}
              onChange={(e) => setFreeReason(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<CardGiftcardIcon />}
            disabled={
              !freeReason.trim() ||
              !freeDays ||
              parseInt(freeDays, 10) < 1 ||
              !activeLikeSub ||
              grantFreePeriodMutation.isPending
            }
            onClick={() => grantFreePeriodMutation.mutate()}
          >
            {grantFreePeriodMutation.isPending ? 'Granting…' : 'Grant'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Billing Options */}
      <Dialog open={activeDialog === 'billingOptions'} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Billing Options</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={chargeMonthlyOnly}
                  onChange={(e) => setChargeMonthlyOnly(e.target.checked)}
                />
              }
              label="Charge monthly fee only (waive usage / per-ton / per-job charges)"
            />
            <TextField
              fullWidth
              label="Bonus included tons (extra quota)"
              type="number"
              value={bonusTons}
              onChange={(e) => setBonusTons(e.target.value)}
              inputProps={{ min: 0, step: '0.01' }}
              helperText="Extra tons allowed above the plan quota before overage fees"
            />
            <TextField
              fullWidth
              label="Bonus included jobs (extra quota)"
              type="number"
              value={bonusJobs}
              onChange={(e) => setBonusJobs(e.target.value)}
              inputProps={{ min: 0 }}
            />
            <TextField
              fullWidth
              label="Overage discount %"
              type="number"
              value={overageDiscount}
              onChange={(e) => setOverageDiscount(e.target.value)}
              inputProps={{ min: 0, max: 100, step: '0.01' }}
              helperText="Discount on usage billed beyond the (plan + bonus) quota"
            />
            <TextField
              fullWidth
              required
              label="Reason"
              value={billingReason}
              onChange={(e) => setBillingReason(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<TuneIcon />}
            disabled={!billingReason.trim() || !activeLikeSub || setBillingOptionsMutation.isPending}
            onClick={() => setBillingOptionsMutation.mutate()}
          >
            {setBillingOptionsMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Temporary Plan Bounce */}
      <Dialog open={activeDialog === 'bouncePlan'} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Temporary Plan Bounce</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Temporarily move the account to another plan. It reverts automatically to the
              current plan when the bounce expires.
            </Typography>
            <TextField
              select
              fullWidth
              required
              label="Bounce to plan"
              value={bouncePlanId}
              onChange={(e) => setBouncePlanId(e.target.value)}
            >
              {eligiblePlans.length === 0 ? (
                <MenuItem value="" disabled>
                  No plans for {accountTypeUpper || 'this account type'}
                </MenuItem>
              ) : (
                eligiblePlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.name}
                    {plan.planCode ? ` (${plan.planCode})` : ''}
                  </MenuItem>
                ))
              )}
            </TextField>
            <TextField
              fullWidth
              label="Duration (days)"
              type="number"
              value={bounceDays}
              onChange={(e) => setBounceDays(e.target.value)}
              inputProps={{ min: 1, max: 3650 }}
            />
            <TextField
              fullWidth
              required
              label="Reason"
              value={bounceReason}
              onChange={(e) => setBounceReason(e.target.value)}
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<TrendingUpIcon />}
            disabled={
              !bouncePlanId ||
              !bounceReason.trim() ||
              !bounceDays ||
              parseInt(bounceDays, 10) < 1 ||
              !activeLikeSub ||
              bouncePlanMutation.isPending
            }
            onClick={() => bouncePlanMutation.mutate()}
          >
            {bouncePlanMutation.isPending ? 'Bouncing…' : 'Bounce'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
